import {Box, Button, Typography, CircularProgress, IconButton} from '@mui/material';
import {useState, useRef, useEffect, useCallback} from 'react';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import message from 'src/components/Message';
import {uploadDeviceLogo, getDeviceLogo, deleteDeviceLogo} from 'src/apis/autoGenerate/services';

interface LogoUploadProps {
  deviceId?: string;
  onLogoChange?: (logoUrl: string | null) => void;
}

export default function LogoUpload({deviceId, onLogoChange}: LogoUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadLogo = useCallback(async () => {
    if (!deviceId) return;

    setLoading(true);
    try {
      const response = await getDeviceLogo(deviceId);
      if (response.data?.logo?.logoUrl) {
        setLogoUrl(response.data.logo.logoUrl);
        onLogoChange?.(response.data.logo.logoUrl);
      }
    } catch (err: any) {
      // Only show error if it's not a 404 (no logo exists)
      if (err.response?.status !== 404) {
        console.error('Failed to load logo:', err);
      }
    } finally {
      setLoading(false);
    }
  }, [deviceId, onLogoChange]);

  // Load existing logo when component mounts or deviceId changes
  useEffect(() => {
    if (deviceId) {
      loadLogo();
    }
  }, [deviceId, loadLogo]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      message.error('Please upload a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (max 2MB)
    const maxSize = 2 * 1024 * 1024; // 2MB
    if (file.size > maxSize) {
      message.error('Image size must be less than 2MB');
      return;
    }

    if (deviceId) {
      handleUpload(file);
    }
  };

  const handleUpload = async (file: File) => {
    if (!deviceId) {
      message.error('Device ID is required');
      return;
    }

    setUploading(true);
    try {
      const response = await uploadDeviceLogo(deviceId, file);
      if (response.data?.logo?.logoUrl) {
        setLogoUrl(response.data.logo.logoUrl);
        onLogoChange?.(response.data.logo.logoUrl);
        message.success('Logo uploaded successfully');
      }
    } catch (err: any) {
      message.error(`Failed to upload logo: ${err.message || 'Unknown error'}`);
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDelete = async () => {
    if (!deviceId) return;

    setDeleting(true);
    try {
      await deleteDeviceLogo(deviceId);
      setLogoUrl(null);
      onLogoChange?.(null);
      message.success('Logo deleted successfully');
    } catch (err: any) {
      message.error(`Failed to delete logo: ${err.message || 'Unknown error'}`);
    } finally {
      setDeleting(false);
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  if (!deviceId) {
    return (
      <Box sx={{p: 2, border: '1px dashed #ccc', borderRadius: 1, textAlign: 'center'}}>
        <Typography color="text.secondary">Save the device first to upload a logo</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        style={{display: 'none'}}
      />

      {loading ? (
        <Box sx={{display: 'flex', justifyContent: 'center', p: 2}}>
          <CircularProgress size={24} />
        </Box>
      ) : logoUrl ? (
        <Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 2,
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              mb: 2,
              bgcolor: '#f5f5f5',
              minHeight: 120,
              maxHeight: 200,
              overflow: 'hidden',
            }}
          >
            <img
              src={logoUrl}
              alt="Device Logo"
              style={{
                maxWidth: '100%',
                maxHeight: '180px',
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
              }}
            />
          </Box>
          <Box sx={{display: 'flex', gap: 1}}>
            <Button
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              onClick={handleButtonClick}
              disabled={uploading || deleting}
              fullWidth
            >
              {uploading ? 'Uploading...' : 'Change Logo'}
            </Button>
            <IconButton
              color="error"
              onClick={handleDelete}
              disabled={uploading || deleting}
              sx={{border: '1px solid', borderColor: 'error.main'}}
            >
              {deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
            </IconButton>
          </Box>
        </Box>
      ) : (
        <Button
          variant="outlined"
          startIcon={uploading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
          onClick={handleButtonClick}
          disabled={uploading}
          fullWidth
        >
          {uploading ? 'Uploading...' : 'Upload Logo'}
        </Button>
      )}

      <Typography variant="caption" color="text.secondary" sx={{mt: 1, display: 'block'}}>
        Supported formats: JPEG, PNG (Max 2MB) Since the page uses a dark background, we recommend
        uploading a light-colored or white-background logo for optimal visibility.
      </Typography>
    </Box>
  );
}
